import { alpha, styled } from '@mui/material/styles';
import UPButton from 'components/button';
import UPInput from 'components/input';
import { LinkButton } from 'components/link-button/styles';
import { MotivationTextArea } from 'modules/job-application/styles';

/**
 * No red/error outlined-button variant exists on the shared UPButton, so Discard gets its own
 * styled wrapper rather than adding a one-off color prop to the shared component. The `outlined`
 * prop is passed at the call site so MUI renders the outlined variant we then recolor to error.
 *
 * The doubled `&&` selector is needed because MUI's own `.MuiButton-outlinedPrimary` rules
 * (which revert toward the green primary color) tie on specificity with a plain override and
 * win on source order — `&&` duplicates our class to outrank it without resorting to !important.
 */
export const DiscardButtonStyled = styled(UPButton)(({ theme }) => ({
  '&&': {
    backgroundColor: 'transparent',
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, theme.palette.action.hoverOpacity),
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main,
    },
  },
}));

export const ReadOnlyInput = styled(UPInput)(({ theme }) => ({
  '.MuiInputBase-input.Mui-disabled': {
    color: theme.palette.secondary.dark,
    WebkitTextFillColor: theme.palette.secondary.dark,
  },
}));

export const DisabledMotivationTextArea = styled(MotivationTextArea)(({ theme }) => ({
  '&:disabled': {
    color: theme.palette.secondary.dark,
    WebkitTextFillColor: theme.palette.secondary.dark,
    opacity: 1,
  },
}));

/**
 * View CV reads as a link rather than a field, so it builds on the shared LinkButton (styled
 * Button) instead of UPButton. Two additions on top of that base: the underline is permanent
 * rather than hover-only, and the color is pinned to the green primary — LinkButton inherits
 * its color, which here would come out as the modal's grey body text.
 *
 * It is styled off LinkButton and not the UPLinkButton wrapper because that wrapper forwards
 * only onClick/text/children/sx, so a `styled()` class on it would never reach the DOM.
 *
 * The 49px height matches UPInputTextField so this cell lines up with the labelled input beside
 * it, with the link centred in that box the way input text is.
 */
export const ViewCvLinkButton = styled(LinkButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '14px',
  fontWeight: 'bold',
  textDecoration: 'underline',
  height: '49px',
  justifyContent: 'flex-start',
  alignItems: 'center',
  '&:hover': {
    textDecoration: 'underline',
  },
}));
